import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import React, { useEffect, useState } from "react";
import { FaFileExcel, FaFilePdf } from "react-icons/fa";
import { getOrderAPI } from "../../../utils/api";
import "./OrderReport.css";

const statusMap = {
  processing: { label: "Processing", color: "#FF9800" },
  delivered: { label: "Delivered", color: "#17BF52" },
  ordered: { label: "Ordered", color: "#0A727E" },
  cancelled: { label: "Cancelled", color: "red" }
};

const paymentStatusMap = {
  1: { label: "Paid" },
  0: { label: "Unpaid" },
};

const OrderReport = ({ showToast }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [reportFilters, setReportFilters] = useState({
    fromDate: new Date().toISOString().split('T')[0],
    toDate: new Date().toISOString().split('T')[0],
    status: "",
    userType: "",
    search: ""
  });

  const handleReportFilterChange = (field, value) => {
    setReportFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const generateExcel = async () => {
    try {
      setLoading(true);

      let requestBody = { is_report: 1 };
      if (reportFilters.fromDate) {
        const d = new Date(reportFilters.fromDate);
        requestBody.from_date = `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
      }
      if (reportFilters.toDate) {
        const d = new Date(reportFilters.toDate);
        requestBody.to_date = `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
      }
      if (reportFilters.status) requestBody.status = reportFilters.status;
      if (reportFilters.userType) requestBody.user_type = reportFilters.userType;
      if (reportFilters.search) requestBody.search = reportFilters.search;

      const response = await getOrderAPI(requestBody);
      if (response?.status !== 200) {
        showToast('Failed to fetch data for Excel', 'error');
        return;
      }

      const reportOrders = response?.data || [];
      if (reportOrders.length === 0) {
        showToast('No data available to export', 'error');
        return;
      }

      const header = [
        'Order No',
        'Customer Name',
        'Phone/Mobile No',
        'User Type',
        'Date',
        'Ship Address',
        'Status',
        'Payment',
        'Product',
        'Color',
        'Size',
        'QTY',
        'Price',
        'Total'
      ];

      const rows = [];
      const merges = [];
      // We will add a title row first, then header, then data. So data starts at row index 2
      let currentRow = 2; // 0-based sheet indexing: 0=title, 1=header, 2=first data row

      // Simple word-wrapping helpers for multi-line cells
      const insertBreaks = (text, width) => {
        const words = String(text || '').split(' ');
        let line = '';
        const lines = [];
        words.forEach((w) => {
          if ((line + (line ? ' ' : '') + w).length > width) {
            if (line) lines.push(line);
            line = w;
          } else {
            line = line ? line + ' ' + w : w;
          }
        });
        if (line) lines.push(line);
        return lines.join('\n');
      };

      const wrapAddress = (address) => {
        const withLineBreaks = String(address || '').replace(/,\s*/g, ',\n');
        return insertBreaks(withLineBreaks, 40);
      };

      const wrapProduct = (name) => insertBreaks(name, 30);

      reportOrders.forEach((order, index) => {
        const orderNo = String(index + 1);
        const customerName = String(order?.name || order?.user?.name || 'N/A');
        const phone = String((order?.country_code || '') + (order?.contact || ''));
        const userType = String(order?.user?.user_type || order?.user_type || 'N/A');
        const dateStr = String(new Date(order.order_date || order.created_at || Date.now()).toLocaleDateString());
        const shipAddressRaw = String(`${order?.address || ''}${order?.city ? ', ' + order.city : ''}${order?.state ? ', ' + order.state : ''}${order?.pincode ? ' ' + order.pincode : ''}`.trim());
        const shipAddress = wrapAddress(shipAddressRaw);
        const statusLabel = String(statusMap[order.order_status?.toLowerCase()]?.label || order.order_status || 'N/A');
        const paymentLabel = String(paymentStatusMap[order.paid]?.label || 'N/A');

        const products = Array.isArray(order.products) && order.products.length > 0 ? order.products : [null];
        const startRow = currentRow;

        products.forEach((product, pIdx) => {
          const productName = product ? wrapProduct(String(product.product?.name || product.name || 'N/A')) : '';
          const color = product ? String(product.color || '') : '';
          const size = product ? String(product.size || '') : '';
          const qty = product ? String(product.quantity || 1) : '';
          const price = product ? String(parseFloat(product.price || 0)) : '';
          const total = product ? String((parseFloat(product.price || 0) * (product.quantity || 1)).toFixed(2)) : '';

          if (pIdx === 0) {
            rows.push([
              orderNo,
              customerName,
              phone,
              userType,
              dateStr,
              shipAddress,
              statusLabel,
              paymentLabel,
              productName,
              color,
              size,
              qty,
              price,
              total
            ]);
          } else {
            // Leave order-level columns empty for subsequent rows
            rows.push([
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              productName,
              color,
              size,
              qty,
              price,
              total
            ]);
          }
          currentRow += 1;
        });

        const endRow = currentRow - 1;
        if (endRow > startRow) {
          // Merge order-level columns A..H across product rows
          for (let col = 0; col <= 7; col++) {
            merges.push({ s: { r: startRow, c: col }, e: { r: endRow, c: col } });
          }
        }
      });

      // Build title row text
      const generatedText = `Generated: ${new Date().toLocaleDateString()}`;
      const rangeText = `Date Range: ${reportFilters.fromDate} to ${reportFilters.toDate}`;
      const titleRow = [`${generatedText}      ${rangeText}`];

      const worksheet = XLSX.utils.aoa_to_sheet([titleRow, header, ...rows]);
      // Merge title row across all columns (A..N)
      merges.unshift({ s: { r: 0, c: 0 }, e: { r: 0, c: header.length - 1 } });
      if (merges.length) worksheet['!merges'] = merges;
      // Style merged title cell: center text and bold
      if (worksheet['A1']) {
        worksheet['A1'].s = {
          font: { bold: true },
          alignment: { horizontal: 'center', vertical: 'center', wrapText: true }
        };
      }
      // Optional: increase title row height a bit
      worksheet['!rows'] = worksheet['!rows'] || [];
      worksheet['!rows'][0] = { hpt: 20 };
      const colWidths = header.map((h, idx) => {
        const maxLen = Math.max(
          h.length,
          ...rows.map((r) => (r[idx] ? String(r[idx]).length : 0))
        );
        let wch = Math.min(Math.max(maxLen + 2, 10), 50);
        if (idx === 5) wch = Math.max(wch, 40); // Ship Address column F
        if (idx === 8) wch = Math.max(wch, 35); // Product column I
        return { wch };
      });
      worksheet['!cols'] = colWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Orders');
      const fileName = `order-report-${reportFilters.fromDate}_to_${reportFilters.toDate}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      showToast('Excel exported successfully', 'success');
    } catch (err) {
      console.error('Error exporting Excel:', err);
      showToast('Failed to export Excel', 'error');
    } finally {
      setLoading(false);
    }
  };

  const clearReportFilters = () => {
    setReportFilters({
      fromDate: new Date().toISOString().split('T')[0],
      toDate: new Date().toISOString().split('T')[0],
      status: "",
      userType: "",
      search: ""
    });
    fetchReportData({
      fromDate: new Date().toISOString().split('T')[0],
      toDate: new Date().toISOString().split('T')[0],
      status: "",
      userType: "",
      search: ""
    });
  };

  const fetchReportData = async (filters) => {
    try {
      setLoading(true);
      setError(null);

      let requestBody = {
        is_report: 1
      };

      if (filters.fromDate) {
        const date = new Date(filters.fromDate);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        requestBody.from_date = `${day}-${month}-${year}`;
      }
      if (filters.toDate) {
        const date = new Date(filters.toDate);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        requestBody.to_date = `${day}-${month}-${year}`;
      }
      if (filters.status) requestBody.status = filters.status;
      if (filters.userType) requestBody.user_type = filters.userType;
      if (filters.search) requestBody.search = filters.search;

      const response = await getOrderAPI(requestBody);

      if (response?.status === 200) {
        setOrders(response?.data || []);
      } else if (response?.message) {
        setError(typeof response?.message === 'string'
          ? response?.message
          : 'Failed due to internal error');
      } else {
        setError('Failed due to internal error');
      }
    } catch (err) {
      console.error('Error fetching report data:', err);
      setError('Failed due to internal error');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async () => {
    try {
      setLoading(true);
      let requestBody = {
        is_report: 1
      };

      if (reportFilters.fromDate) {
        const date = new Date(reportFilters.fromDate);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        requestBody.from_date = `${day}-${month}-${year}`;
      }
      if (reportFilters.toDate) {
        const date = new Date(reportFilters.toDate);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        requestBody.to_date = `${day}-${month}-${year}`;
      }
      if (reportFilters.status) requestBody.status = reportFilters.status;
      if (reportFilters.userType) requestBody.user_type = reportFilters.userType;
      if (reportFilters.search) requestBody.search = reportFilters.search;

      const response = await getOrderAPI(requestBody);

      if (response?.status === 200) {
        const reportOrders = response?.data || [];
        setOrders(reportOrders);
      } else {
        showToast("Failed to fetch data for PDF generation", "error");
      }
    } catch (err) {
      console.error('Error generating PDF:', err);
      showToast("Failed to generate PDF", "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchReportData({
      fromDate: new Date().toISOString().split('T')[0],
      toDate: new Date().toISOString().split('T')[0],
      status: "",
      userType: "",
      search: ""
    });
  }, []);

  return (
    <div className="order-report">
      <div className="report-filter-row">
        <div className="report-filter-group">
          <label>From Date</label>
          <input
            type="date"
            value={reportFilters.fromDate}
            onChange={(e) => handleReportFilterChange('fromDate', e.target.value)}
            className="report-filter-input"
            max={new Date().toISOString().split('T')[0]}
          />
        </div>
        <div className="report-filter-group">
          <label>To Date</label>
          <input
            type="date"
            value={reportFilters.toDate}
            onChange={(e) => handleReportFilterChange('toDate', e.target.value)}
            className="report-filter-input"
            max={new Date().toISOString().split('T')[0]}
            min={reportFilters.fromDate}
          />
        </div>
        <div className="report-filter-group">
          <label>Status</label>
          <select
            value={reportFilters.status}
            onChange={(e) => handleReportFilterChange('status', e.target.value)}
            className="report-filter-select"
          >
            <option value="">All Status</option>
            <option value="ordered">Ordered</option>
            <option value="processing">Processing</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div className="report-filter-group">
          <label>User Type</label>
          <select
            value={reportFilters.userType}
            onChange={(e) => handleReportFilterChange('userType', e.target.value)}
            className="report-filter-select"
          >
            <option value="">All Users</option>
            <option value="wholeseller">Wholeseller</option>
            <option value="retailer">Retailer</option>
          </select>
        </div>
        <div className="report-filter-group">
          <label>Search</label>
          <input
            type="text"
            placeholder="Search by order ID, customer name, etc."
            value={reportFilters.search}
            onChange={(e) => handleReportFilterChange('search', e.target.value)}
            className="report-filter-input"
          />
        </div>
      </div>
      <div className="report-filter-actions">
        <button className="report-clear-btn" onClick={clearReportFilters}>
          Clear
        </button>
        <button className="report-generate-btn" onClick={onSubmit}>
          Submit
        </button>
        <button className="report-generate-btn" onClick={generateExcel}>
        <FaFileExcel />
         Generate Excel
        </button>
      </div>

      {loading && (
        <div className="report-loading-container">
          <div className="report-loading-spinner"></div>
          <p>Loading report data...</p>
        </div>
      )}

      {error && (
        <div className="report-error-container">
          <div style={{ color: "#dc2626", fontSize: "1.25rem", marginBottom: "1rem" }}>⚠️</div>
          <p style={{ color: "#dc2626", fontSize: "1.125rem" }}>{error}</p>
        </div>
      )}

      {!loading && !error && (
        <div className="report-summary-cards">
          <div className="summary-card">
            <h3>Total Orders</h3>
            <p>{orders.length}</p>
          </div>
          <div className="summary-card">
            <h3>Total Amount</h3>
            <p>₹{orders.reduce((sum, order) => sum + (parseFloat(order.total) || 0), 0).toFixed(2)}</p>
          </div>
          <div className="summary-card">
            <h3>Average Order Value</h3>
            <p>₹{orders.length > 0 ? (orders.reduce((sum, order) => sum + (parseFloat(order.total) || 0), 0) / orders.length).toFixed(2) : '0.00'}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderReport;
